/**
 * Senal de scroll horizontal, para toda la app.
 *
 * Varias zonas se deslizan de lado —tablas anchas, barras de pestanas— pero nada lo
 * avisaba: se leia como contenido cortado, no como algo que se puede mover. Esto marca
 * cada zona deslizable con `data-mas-derecha` / `data-mas-izquierda`, y el CSS se encarga
 * de mostrar el desvanecido y la flecha SOLO hacia el lado donde queda contenido.
 *
 * Vive en un unico modulo y se instala una vez, en vez de repetir el mismo estado en cada
 * pantalla: las zonas deslizables estan repartidas en media docena de paginas y aparecen y
 * desaparecen segun lo que se este mirando.
 *
 * Se aplica a `.cc-scroll-x` y tambien a cualquier `overflow-x-auto` de Tailwind, para
 * cubrir las que ya existian sin tener que tocarlas una por una.
 */

const SELECTOR = '.cc-scroll-x, .overflow-x-auto';
const MARGEN = 2;   // holgura para que un pixel de redondeo no encienda la senal

function marcar(el) {
  const restaDerecha = el.scrollWidth - el.clientWidth - el.scrollLeft;
  const hayDerecha = restaDerecha > MARGEN;
  const hayIzquierda = el.scrollLeft > MARGEN;

  el.toggleAttribute('data-mas-derecha', hayDerecha);
  el.toggleAttribute('data-mas-izquierda', hayIzquierda);
  if (hayDerecha) el.setAttribute('data-mas-derecha', '1');
  if (hayIzquierda) el.setAttribute('data-mas-izquierda', '1');

  // Una franja que se desliza de lado no deberia arrastrar la pagina hacia arriba y
  // abajo al mismo tiempo: el dedo sale en diagonal y se pierden las dos cosas. Se
  // restringe SOLO en las franjas bajas —barras de pestanas, filas de filtros—, porque
  // en una tabla alta el dedo tiene que poder subir y bajar desde dentro.
  el.toggleAttribute('data-solo-horizontal', el.clientHeight <= 120);

  // La flecha va en el CONTENEDOR y no dentro de la zona que se mueve: si fuera dentro,
  // se desplazaria junto con el contenido y dejaria de apuntar a nada.
  const padre = el.parentElement;
  if (padre) {
    padre.classList.add('cc-scroll-wrap');
    if (hayDerecha) {
      padre.setAttribute('data-mas-derecha', '1');
      // El padre puede ser mucho mas grande que la franja: una tarjeta entera, o media
      // pantalla. Con la flecha en `top:50%; right:6px` del padre, terminaba flotando en
      // cualquier parte, lejos de la zona que efectivamente se desliza. Aca se le pasa la
      // posicion real de la franja para que quede justo sobre ella.
      //
      // Se mide con getBoundingClientRect y no con offsetTop: offsetTop es relativo al
      // offsetParent, que no tiene por que ser este padre.
      const rp = padre.getBoundingClientRect();
      const re = el.getBoundingClientRect();
      padre.style.setProperty('--cc-hint-top', `${Math.round(re.top - rp.top + re.height / 2)}px`);
      padre.style.setProperty('--cc-hint-right', `${Math.round(rp.right - re.right + 6)}px`);
    } else {
      padre.removeAttribute('data-mas-derecha');
      padre.style.removeProperty('--cc-hint-top');
      padre.style.removeProperty('--cc-hint-right');
    }
  }
}

function revisarTodo() {
  document.querySelectorAll(SELECTOR).forEach(marcar);
}

export function instalarSenalDeScroll() {
  if (typeof window === 'undefined' || window.__ccScrollHint) return;
  window.__ccScrollHint = true;

  // Al desplazar, solo se recalcula la zona que se movio.
  document.addEventListener('scroll', (e) => {
    const el = e.target;
    if (el instanceof Element && el.matches?.(SELECTOR)) marcar(el);
  }, true);

  // Al cambiar el tamano de una zona (girar el telefono, abrir una pestana) se recalcula
  // esa zona. ResizeObserver evita tener que revisar la pagina entera todo el tiempo.
  const ro = new ResizeObserver(entradas => entradas.forEach(x => marcar(x.target)));
  const observadas = new WeakSet();

  const engancharNuevas = () => {
    document.querySelectorAll(SELECTOR).forEach(el => {
      if (!observadas.has(el)) { observadas.add(el); ro.observe(el); }
      marcar(el);
    });
  };

  // La app es una SPA: las zonas deslizables aparecen y desaparecen al navegar, asi que
  // hay que enterarse de los cambios del DOM en vez de revisar solo al cargar.
  const mo = new MutationObserver(() => {
    clearTimeout(window.__ccScrollHintT);
    window.__ccScrollHintT = setTimeout(engancharNuevas, 120);
  });
  mo.observe(document.body, { childList: true, subtree: true });

  window.addEventListener('resize', revisarTodo);
  engancharNuevas();
}
